import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, Check, X } from "@phosphor-icons/react";
import { getOutletId, clearOutletId } from "../utils/appNavigation";

export default function AppOutletConfig() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const params = new URLSearchParams(location.search);
  const outletId = getOutletId(params);
  
  const [outlet, setOutlet] = useState(null);
  const [name, setName] = useState("");
  const [reviewUrl, setReviewUrl] = useState("");
  const [requireScreenshot, setRequireScreenshot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!outletId) {
      navigate("/app/settings");
      return;
    }

    const fetchOutlet = async () => {
      try {
        const { data } = await api.get(`/outlets/${outletId}`);
        
        if (!data) {
          clearOutletId();
          navigate("/app/settings");
          return;
        }
        
        setOutlet(data);
        setName(data.name || "");
        setReviewUrl(data.google_review_url || "");
        setRequireScreenshot(data.require_screenshot || false);
      } catch (e) {
        setError("Failed to load outlet");
        toast.error("Could not load outlet data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchOutlet();
  }, [outletId, navigate]);

  const [validationError, setValidationError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    if (outlet) {
      const nameChanged = String(name || "").trim() !== String(outlet.name || "").trim();
      const urlChanged = String(reviewUrl || "").trim() !== String(outlet.google_review_url || "").trim();
      const toggleChanged = requireScreenshot !== (outlet.require_screenshot || false);
      setHasChanges(nameChanged || urlChanged || toggleChanged);
    }
  }, [name, reviewUrl, requireScreenshot, outlet]);

  const handleSave = async () => {
    const nameClean = String(name || "").trim();
    const urlClean = String(reviewUrl || "").trim();
    
    // Validation
    if (!nameClean) {
      setValidationError("Outlet name is required");
      return;
    }
    if (!urlClean) {
      setValidationError("Google Review URL is required");
      return;
    }
    
    // URL validation
    try {
      new URL(urlClean);
    } catch {
      setValidationError("Enter a valid URL (include https://)");
      return;
    }
    
    setValidationError(null);
    setSaving(true);
    
    try {
      await api.put(`/outlets/${outletId}`, {
        name: nameClean,
        google_review_url: urlClean,
        require_screenshot: requireScreenshot,
      });
      // Update local state to reflect saved changes
      setOutlet(prev => ({ 
        ...prev, 
        name: nameClean, 
        google_review_url: urlClean, 
        require_screenshot: requireScreenshot 
      }));
      setHasChanges(false);
      toast.success("Changes saved");
    } catch (e) {
      setValidationError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (hasChanges) {
      if (!window.confirm("Discard changes?")) {
        return;
      }
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/app/settings");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <div className="tag">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-5" style={{ background: "var(--bg)" }}>
        <div className="card-pop p-8 text-center max-w-sm">
          <h2 className="display text-xl font-black mb-3">Error</h2>
          <p className="text-[var(--ink-muted)] mb-4">{error}</p>
          <button onClick={() => navigate("/app/settings")} className="btn-primary">
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <header className="sticky top-0 z-10 px-5 py-4 flex items-center gap-3" style={{ background: "var(--bg)", borderBottom: "1.5px solid var(--ink)" }}>
        <button
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center rounded-lg transition-all hover:opacity-70"
          style={{ border: "1.5px solid var(--ink)" }}
          aria-label="Back"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
        <h1 className="display text-xl font-black">Configure</h1>
      </header>

      {/* Main Content */}
      <main className="p-5 max-w-xl mx-auto w-full pb-8">
        <div className="card-pop p-5 space-y-5">
          {/* Outlet Name */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2 block">
              Outlet Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Restaurant"
              className="input-pop w-full"
            />
          </div>

          {/* Review URL */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2 block">
              Google Review URL
            </label>
            <input
              type="url"
              value={reviewUrl}
              onChange={(e) => setReviewUrl(e.target.value)}
              placeholder="https://g.page/r/..."
              className="input-pop w-full"
            />
            <p className="text-xs text-[var(--ink-muted)] mt-1">
              Find this in Google Business Profile
            </p>
          </div>

          {/* Screenshot Toggle */}
          <div className="pt-2">
            <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-3 block">
              Verification
            </label>
            <button
              onClick={() => setRequireScreenshot(!requireScreenshot)}
              className="w-full card-pop p-4 flex items-center justify-between transition-all"
              style={{ 
                background: requireScreenshot ? "var(--pastel-green)" : "var(--secondary)",
                borderColor: "var(--ink)"
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="h-10 w-10 rounded-xl flex items-center justify-center"
                  style={{ 
                    background: requireScreenshot ? "var(--success)" : "var(--ink-muted)",
                    color: "#fff"
                  }}
                >
                  {requireScreenshot ? <Check size={20} weight="bold" /> : <X size={20} weight="bold" />}
                </div>
                <div className="text-left">
                  <div className="font-bold">Require Screenshot</div>
                  <div className="text-xs text-[var(--ink-muted)]">
                    Customers must upload review proof
                  </div>
                </div>
              </div>
              <div 
                className="w-12 h-6 rounded-full relative transition-colors"
                style={{ background: requireScreenshot ? "var(--success)" : "var(--ink-muted)" }}
              >
                <span 
                  className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                  style={{ left: requireScreenshot ? "calc(100% - 20px)" : "4px" }}
                />
              </div>
            </button>
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="p-3 rounded-xl text-sm font-semibold" style={{ background: "#FFE5E2", color: "var(--error)" }}>
              {validationError}
            </div>
          )}

          {/* Save Button */}
          <div className="pt-2 sticky bottom-0" style={{ background: "var(--bg)", paddingBottom: "env(safe-area-inset-bottom, 12px)" }}>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary w-full justify-center"
            >
              <FloppyDisk size={18} weight="bold" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Note about full editor */}
        <div className="mt-5 text-center pb-4">
          <p className="text-sm text-[var(--ink-muted)]">
            For rewards & advanced settings, open full dashboard
          </p>
          <button 
            onClick={() => window.location.href = "/dashboard"}
            className="text-sm font-bold text-[var(--primary)] mt-2 underline"
          >
            Open Dashboard
          </button>
        </div>
      </main>
    </div>
  );
}